import type { UIMessage } from 'ai';
import axios from 'axios';
import { z } from 'zod';
import type { CommandInfo } from '@rossum/fabry-api-client';
import { getAuthToken } from '../../../lib/token';
import { fabryApi, getApiUrl, getFabryRequestConfig } from '../api';

export const getAgentHeaders = () => ({
  'Content-Type': 'application/json',
  'X-Rossum-Token': getAuthToken() ?? '',
  'X-Rossum-Api-Url': getApiUrl(),
});

export const createChat = async () => {
  const { data } = await fabryApi.createChatApiV1ChatsPost(
    getFabryRequestConfig()
  );
  return data;
};

export const fetchChatMessages = async (chatId: string) => {
  const { data } = await fabryApi.getChatApiV1ChatsChatIdGet(
    chatId,
    getFabryRequestConfig()
  );
  return data;
};

// mr-fabry only. Returns the thread history as canonical AI SDK v6 UIMessage[]
// (tool-call, reasoning, text and file parts intact) — not part of the generated
// client, which is generated from the legacy service's OpenAPI spec.
export const fetchThreadMessages = async (
  chatId: string
): Promise<UIMessage[]> => {
  const { data } = await axios.get<UIMessage[]>(
    `/api/v2/threads/${chatId}/messages`,
    getFabryRequestConfig()
  );
  return data;
};

export const listCommands = async (): Promise<CommandInfo[]> => {
  try {
    const { data } = await fabryApi.listCommandsApiV1CommandsGet(
      getFabryRequestConfig()
    );
    return data.commands;
  } catch {
    return [];
  }
};

export const submitFeedback = async (
  chatId: string,
  turnIndex: number,
  isPositive: boolean,
  rossumUrl: string
) => {
  const { data } = await fabryApi.submitFeedbackApiV1ChatsChatIdFeedbackPut(
    chatId,
    { turn_index: turnIndex, is_positive: isPositive, rossum_url: rossumUrl },
    getFabryRequestConfig()
  );
  return data;
};

export const downloadChatFile = async (
  chatId: string,
  filename: string
): Promise<Blob> => {
  const { data } = await fabryApi.downloadFileApiV1ChatsChatIdFilesFilenameGet(
    chatId,
    filename,
    { ...getFabryRequestConfig(), responseType: 'blob' }
  );
  return z.instanceof(Blob).parse(data);
};
